/**
 * The wire contract between the app and the API.
 *
 * This package exists for one reason: the client and the server must agree,
 * byte for byte, on what was signed. If `canonicalize` were implemented twice —
 * once in the browser and once in Node — the two would eventually drift, and
 * the symptom would be a uniform "invalid signature" that looks like a crypto
 * bug and is actually a whitespace bug. So it is written once, here, and both
 * sides import it.
 */

export const PROTOCOL_VERSION = '1';

/** Consent scopes. Adding one here requires a migration to widen the CHECK. */
export const CONSENT_SCOPES = Object.freeze(['corpus', 'cohort', 'routing', 'assistant']);

/**
 * Deterministic JSON serialisation.
 *
 * Object keys sorted; `undefined` dropped rather than emitted as null; arrays
 * kept in order because their order is meaningful. Numbers go through the
 * standard JSON representation, which is stable across V8 and JavaScriptCore
 * for every value this protocol carries.
 */
export function canonicalize(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value ?? null);
  if (Array.isArray(value)) return `[${value.map(canonicalize).join(',')}]`;
  const keys = Object.keys(value)
    .filter((k) => value[k] !== undefined)
    .sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalize(value[k])}`).join(',')}}`;
}

/** The exact payload signed when registering a device. */
export function registrationPayload({ publicKey, nonce, issuedAt }) {
  return { publicKey, nonce, issuedAt };
}

/** The exact payload signed when recording a consent decision. */
export function consentPayload({ deviceId, nonce, issuedAt, scope, granted }) {
  return { deviceId, nonce, issuedAt, scope, granted };
}

/** The exact payload signed when donating a reading. */
export function contributionPayload({ deviceId, nonce, issuedAt, reading }) {
  return { deviceId, nonce, issuedAt, reading };
}

/**
 * Normalise an engine result into the wire shape.
 *
 * Notably it does NOT pass through a heart rate when the scan was refused —
 * the invariant is enforced at every layer: here, in the request schema, in the
 * route handler, and in a CHECK constraint on the table. Four times is not
 * paranoid for the one property the product is built on.
 */
export function readingFromScan(result, { monkTone, observedAt, appVersion, deviceClass } = {}) {
  if (!result) throw new Error('readingFromScan: no scan result');
  const locked = Boolean(result.locked);
  return {
    monkTone,
    locked,
    seconds: round(result.seconds, 2),
    observedAt: observedAt ?? new Date().toISOString(),
    heartRateBpm: locked ? round(result.heartRateBpm, 1) : null,
    breathsPerMinute: locked ? round(result.breathsPerMinute, 1) : null,
    variabilityMs: locked ? round(result.variabilityMs, 1) : null,
    prominence: round(result.prominence, 2),
    perfusionIndex: round(result.perfusionIndex, 8),
    conventionalBpm: round(result.conventionalBpm, 1),
    // The engine's own evidence. Sent for refusals as well as locks — a refusal
    // without a reason teaches a researcher nothing, and refusals are the rows
    // this corpus exists to collect.
    harmonic: round(result.harmonic, 2),
    agreement: round(result.agreement, 3),
    methodSpreadBpm: round(result.methodSpreadBpm, 1),
    frameAcceptance: round(result.frameAcceptance, 3),
    regions: Number.isFinite(result.regions) ? result.regions : null,
    scannerVersion: result.scannerVersion ?? null,
    engineVersion: result.engineVersion,
    appVersion: appVersion ?? null,
    deviceClass: deviceClass ?? null,
  };
}

function round(v, digits) {
  if (v === null || v === undefined || !Number.isFinite(v)) return null;
  return Number(v.toFixed(digits));
}

/** Base64url without padding, from bytes. Used for nonces and ids. */
export function toBase64Url(bytes) {
  let binary = '';
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  for (let i = 0; i < arr.length; i++) binary += String.fromCharCode(arr[i]);
  const b64 = typeof btoa === 'function' ? btoa(binary) : Buffer.from(arr).toString('base64');
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromBase64(value) {
  const normalised = String(value).replace(/-/g, '+').replace(/_/g, '/');
  if (typeof atob === 'function') {
    const binary = atob(normalised);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
  }
  return new Uint8Array(Buffer.from(normalised, 'base64'));
}

/** A fresh 128-bit nonce. */
export function newNonce(cryptoImpl = globalThis.crypto) {
  const bytes = new Uint8Array(16);
  cryptoImpl.getRandomValues(bytes);
  return toBase64Url(bytes);
}
